import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  Outlet,
} from "react-router-dom";
import { Provider, useDispatch, useSelector } from "react-redux";
import store from "./redux/store";
import LoginPage from "./pages/LoginPage";
import { logout } from "./redux/actions/grievanceActions1";
import { FORCE_LOGOUT_KEY } from "./utils/sessionUtils";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import "./styles/variables.css";
import "./styles/global.css";
import "./styles/fileAttachment.css";
import "./styles/timeline.css";
import "./styles/updatedlogin.css";
import "./styles/GrievanceDetails.css";
import "./styles/grants.css";
import "./styles/Dashboard.css";
import Header from "./components/common/Header";
import Sidebar from "./components/common/Sidebar";
import Dashboard from "./components/Dashboard/Dashboard";
import GrievanceList from "./components/GrievanceList/GrievanceList";
import GrantList from "./components/GrantsList/GrantList";
import AresaUserList from "./components/AresaUserList/AresaUserList";
import AresaUserDetails from "./components/AresaUserDetails/AresaUserDetails";
import CenterList from "./components/CenterList/CenterList";
import CenterDetails from "./components/CenterDetails/CenterDetails";
import EventList from "./components/EventList/EventList";
import PolicyList from "./components/Policy/PolicyList";

import EscalatedGrievances from "./components/EscalatedGrievances/EscalatedGrievances";
import EscalatedGrants from "./components/EscalatedGrants/EscalatedGrants";

import EscalationMetrics from "./components/EsclationMetrices/EsclationMetrices";
import GrantDetails from "./components/GrantDetails /GrantDetails";
import GrievanceDetails from "./components/GrievanceDetails/GrievanceDetails";
import EscalatedGrievancesDetails from "./components/EscalatedGrievancesDetails/EscalatedGrievancesDetails";
import EscalatedGrantsDetails from "./components/EscalatedGrantsDetails/EscalatedGrantsDetails";
import IdCardList from "./components/IDCardList/IdCardList";
import IdCardDetails from "./components/IDCardDetails/IdCardDetails";
import ChangePassword from "./components/ChangePassword/ChangePassword";
import Registration from "./components/Registration/Registration";
import UpdateCenterList from "./components/UpdateCenterList/UpdateCenterList";
import { canAccessRoute } from "./utils/roleAccess";

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { user } = useSelector((state) => state.grievances);

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

/**
 * Syncs logout across same-browser tabs after password change / logout (CWE-613).
 * `storage` events only fire in *other* tabs when localStorage changes.
 */
const SessionGuard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.grievances);

  useEffect(() => {
    const handleStorage = (event) => {
      const authCleared =
        (event.key === "token" || event.key === "user") &&
        event.newValue === null;
      const forceLogoutSignaled =
        event.key === FORCE_LOGOUT_KEY && event.newValue != null;

      if (!authCleared && !forceLogoutSignaled) return;
      if (!user && !localStorage.getItem("token")) return;

      dispatch(logout());
      navigate("/", { replace: true });
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [dispatch, navigate, user]);

  return null;
};

// Layout component with header and sidebar
const DashboardLayout = () => {
  const { user } = useSelector((state) => state.grievances);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);

  // Track screen size for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    // Set initial state
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!canAccessRoute(user, location.pathname)) {
    return <Navigate to="/Dashboard" replace />;
  }

  return (
    <div className="dashboard-container">
      <Header />
      <div className="d-flex">
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(!sidebarOpen)}
        />
        <main className="main-content">
          <div className="dashboard-content">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <Provider store={store}>
      <Router>
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable
          theme="light"
        />
        <SessionGuard />
        <Routes>
          <Route path="/" element={<LoginPage />} />

          {/* Protected Branch routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/Dashboard" element={<Dashboard />} />
            <Route path="/GrievanceList" element={<GrievanceList />} />
            <Route path="/GrantList" element={<GrantList />} />
            <Route path="/AresaUserList" element={<AresaUserList />} />
            <Route path="/AresaUserDetails" element={<AresaUserDetails />} />
            <Route path="/CenterList" element={<CenterList />} />
            <Route path="/CenterDetails" element={<CenterDetails />} />
            <Route path="/EventList" element={<EventList />} />
            <Route path="/PolicyList" element={<PolicyList />} />
            <Route path="/EscalationMetrics" element={<EscalationMetrics />} />
            <Route
              path="/EscalatedGrievances"
              element={<EscalatedGrievances />}
            />
            <Route path="/EscalatedGrants" element={<EscalatedGrants />} />
            <Route path="/GrantDetails" element={<GrantDetails />} />
            <Route path="/GrievanceDetails" element={<GrievanceDetails />} />
            <Route
              path="/EscalatedGrievancesDetails"
              element={<EscalatedGrievancesDetails />}
            />
            <Route
              path="/EscalatedGrantsDetails"
              element={<EscalatedGrantsDetails />}
            />
            <Route path="/UpdateCenterList" element={<UpdateCenterList />} />
            <Route path="/IdCardList" element={<IdCardList />} />
            <Route path="/IdCardDetails" element={<IdCardDetails />} />
            <Route path="/ChangePassword" element={<ChangePassword />} />
            <Route path="/Registration" element={<Registration />} />
          </Route>

          {/* Add other role-based routes if needed */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </Provider>
  );
}

export default App;
